/**
 * HYBEC DECO - Premium Pastel Theme Animations
 * Elegant scroll reveal and interaction animations
 */

(function() {
  'use strict';

  // Mark JS as available for progressive-enhancement CSS (e.g., reveal animations)
  document.documentElement.classList.add('js');

  // ============================================
  // SCROLL REVEAL ANIMATION
  // ============================================
  class ScrollReveal {
    constructor() {
      this.elements = document.querySelectorAll('.reveal');
      this.windowHeight = window.innerHeight;
      this.throttleTimeout = null;
      
      if (this.elements.length > 0) {
        this.init();
      }
    }

    init() {
      // Initial check
      this.checkElements();
      
      // Throttled scroll listener
      window.addEventListener('scroll', () => {
        if (!this.throttleTimeout) {
          this.throttleTimeout = setTimeout(() => {
            this.checkElements();
            this.throttleTimeout = null;
          }, 100);
        }
      });

      // Handle resize
      window.addEventListener('resize', () => {
        this.windowHeight = window.innerHeight;
      });
    }

    checkElements() {
      this.elements.forEach(element => {
        if (this.isInViewport(element)) {
          element.classList.add('revealed');
        }
      });
    }

    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      const triggerPoint = this.windowHeight * 0.85;
      return rect.top < triggerPoint;
    }
  }

  // ============================================
  // SMOOTH SCROLL
  // ============================================
  class SmoothScroll {
    constructor() {
      this.init();
    }

    init() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const href = anchor.getAttribute('href');
          if (href.length > 1) {
            const target = document.querySelector(href);
            if (target) {
              e.preventDefault();
              target.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }
        });
      });
    }
  }

  // ============================================
  // HEADER SCROLL BEHAVIOR
  // ============================================
  class HeaderScroll {
    constructor() {
      this.header = document.querySelector('.premium-header');
      this.isManagedPremiumHeader =
        this.header &&
        this.header.tagName &&
        this.header.tagName.toLowerCase() === 'header-drawer-premium';

      if (this.header && !this.isManagedPremiumHeader) {
        this.init();
      }
    }

    init() {
      let lastScrollY = 0;
      let ticking = false;

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.updateHeader(window.scrollY, lastScrollY);
            lastScrollY = window.scrollY;
            ticking = false;
          });
          ticking = true;
        }
      });
    }

    updateHeader(currentScroll, lastScroll) {
      if (currentScroll > 100) {
        this.header.classList.add('premium-header--scrolled');
      } else {
        this.header.classList.remove('premium-header--scrolled');
      }
    }
  }

  // ============================================
  // PARALLAX EFFECT
  // ============================================
  class Parallax {
    constructor() {
      this.elements = document.querySelectorAll('[data-parallax]');
      if (this.elements.length > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.init();
      }
    }

    init() {
      window.addEventListener('scroll', () => {
        requestAnimationFrame(() => this.update());
      });
    }

    update() {
      const scrollY = window.scrollY;
      this.elements.forEach(element => {
        const speed = parseFloat(element.dataset.parallax) || 0.5;
        const offset = scrollY * speed;
        element.style.transform = `translateY(${offset}px)`;
      });
    }
  }

  // ============================================
  // IMAGE LAZY LOAD ENHANCEMENT
  // ============================================
  class LazyLoadEnhance {
    constructor() {
      this.images = document.querySelectorAll('img[loading="lazy"]');
      if (this.images.length > 0) {
        this.init();
      }
    }

    init() {
      this.images.forEach(img => {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
        
        // Handle already loaded images
        if (img.complete) {
          img.classList.add('loaded');
        }
      });
    }
  }

  // ============================================
  // CURSOR FOLLOWER (Optional luxury effect)
  // ============================================
  class CursorFollower {
    constructor() {
      this.cursor = null;
      this.enabled = document.body.dataset.cursorFollower === 'true';
      
      if (this.enabled && window.matchMedia('(pointer: fine)').matches) {
        this.init();
      }
    }

    init() {
      // Create cursor element
      this.cursor = document.createElement('div');
      this.cursor.className = 'cursor-follower';
      this.cursor.innerHTML = '<span class="cursor-follower__dot"></span>';
      document.body.appendChild(this.cursor);

      // Track mouse
      document.addEventListener('mousemove', (e) => {
        requestAnimationFrame(() => {
          this.cursor.style.left = e.clientX + 'px';
          this.cursor.style.top = e.clientY + 'px';
        });
      });

      // Hover effects
      document.querySelectorAll('a, button, .premium-category-card').forEach(el => {
        el.addEventListener('mouseenter', () => {
          this.cursor.classList.add('cursor-follower--hover');
        });
        el.addEventListener('mouseleave', () => {
          this.cursor.classList.remove('cursor-follower--hover');
        });
      });
    }
  }

  // ============================================
  // INITIALIZE ON DOM READY
  // ============================================
  function init() {
    new ScrollReveal();
    new SmoothScroll();
    new HeaderScroll();
    new Parallax();
    new LazyLoadEnhance();
    new CursorFollower();

    // Add loaded class to body for CSS animations
    document.body.classList.add('js-loaded');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run after Shopify sections load
  document.addEventListener('shopify:section:load', () => {
    new ScrollReveal();
    new LazyLoadEnhance();
  });

})();
